<template>
  <div v-if="visible" class="rt-overlay" @click.self="handleClose">
    <div class="rt-card">
      <div class="rt-header">
        <div class="rt-title">Select Reasoning Tag</div>
        <div class="rt-subtitle">
          Tag the reasoning error for this demo to save in the student's learning analytics.
        </div>
        <i class="el-icon-close rt-close" @click="handleClose"></i>
      </div>

      <div class="rt-body">
        <div class="rt-options">
          <div
            v-for="tag in tagOptions"
            :key="tag.value"
            class="rt-option-item"
            :class="{ 'rt-option-selected': selectedTag === tag.value }"
            @click="selectedTag = tag.value"
          >
            <div class="rt-radio-wrapper">
              <span
                class="rt-radio-circle"
                :class="{ 'rt-radio-checked': selectedTag === tag.value }"
              ></span>
            </div>
            <div class="rt-option-content">
              <div class="rt-tag-title" :style="{ color: tag.color }">
                {{ tag.label }}
              </div>
              <div class="rt-tag-desc">{{ tag.description }}</div>
            </div>
          </div>
        </div>
      </div>

      <div class="rt-footer">
        <button class="rt-cancel-btn" @click="handleClose" :disabled="saving">
          SKIP
        </button>
        <button
          class="rt-save-btn"
          :disabled="!selectedTag || saving"
          @click="handleSave"
        >
          <i v-if="saving" class="el-icon-loading"></i>
          SAVE & FINISH DEMO
        </button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ReasoningTagDialog',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    sid: {
      type: String,
      required: true
    },
    studentId: {
      type: String,
      default: ''
    },
    questionId: {
      type: String,
      default: ''
    },
    abstractData: {
      type: [Object, String, Array],
      default: () => ({})
    },
    teacherUid: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      selectedTag: '',
      saving: false,
      tagOptions: [
        {
          label: 'Careless Calculation',
          value: 'Careless Calculation',
          color: '#e53e3e',
          description: 'The student understands the mathematical concept; the error is purely a typo or an arithmetic slip.'
        },
        {
          label: 'Misreading / Text Comprehension',
          value: 'Misreading / Text Comprehension',
          color: '#ed8936',
          description: 'The student’s abstract logic and mathematical reasoning are intact; they simply failed at initial information extraction.'
        },
        {
          label: 'Weak Logic',
          value: 'Weak Logic',
          color: '#ecc94b',
          description: 'The student cannot independently translate text into mathematical symbols and must rely on semi-concrete visual models.'
        },
        {
          label: 'Concept Unclear',
          value: 'Concept Unclear',
          color: '#dd6b20',
          description: 'Fundamental deficiency in underlying mathematical concepts. Triggers remedial exercises in post-class homework.'
        }
      ]
    }
  },
  methods: {
    handleClose() {
      if (this.saving) return
      this.$emit('close')
    },
    async handleSave() {
      if (!this.selectedTag) return
      this.saving = true
      try {
        const teacherUid = this.teacherUid || this.$store?.state?.user?._id || ''
        const payload = {
          sid: this.sid,
          uid: this.studentId,
          questionid: this.questionId,
          abstractData: this.abstractData,
          reasoningTag: this.selectedTag,
          teacherUid
        }
        if (window.App && window.App.service) {
          await window.App.service('student-reasoning-tag').create(payload)
        }
        if (this.$message) {
          this.$message.success('Reasoning tag saved successfully!')
        }
        this.$emit('saved', this.selectedTag)
        this.$emit('close')
      } catch (err) {
        console.error('Error saving reasoning tag:', err)
        if (this.$message) {
          this.$message.error('Failed to save reasoning tag. Please try again.')
        }
      } finally {
        this.saving = false
      }
    }
  }
}
</script>

<style scoped>
.rt-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(15, 23, 42, 0.6);
  backdrop-filter: blur(4px);
  z-index: 1000010;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 16px;
}

.rt-card {
  background: #ffffff;
  border-radius: 16px;
  width: 540px;
  max-width: 95vw;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.15), 0 8px 10px -6px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  position: relative;
  animation: rtScaleIn 0.25s ease-out;
}

@keyframes rtScaleIn {
  from {
    opacity: 0;
    transform: scale(0.95);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

.rt-header {
  background: linear-gradient(135deg, #4f46e5 0%, #3730a3 100%);
  color: #ffffff;
  padding: 24px;
  position: relative;
}

.rt-title {
  font-size: 20px;
  font-weight: 700;
  margin-bottom: 6px;
}

.rt-subtitle {
  font-size: 13px;
  color: #c7d2fe;
  line-height: 1.4;
}

.rt-close {
  position: absolute;
  top: 20px;
  right: 20px;
  font-size: 20px;
  cursor: pointer;
  color: #a5b4fc;
  transition: color 0.2s;
}

.rt-close:hover {
  color: #ffffff;
}

.rt-body {
  padding: 20px 24px;
  max-height: 60vh;
  overflow-y: auto;
}

.rt-options {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.rt-option-item {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  border: 1.5px solid #e2e8f0;
  border-radius: 12px;
  padding: 14px 16px;
  cursor: pointer;
  transition: all 0.2s ease;
  background: #ffffff;
}

.rt-option-item:hover {
  border-color: #6366f1;
  background-color: #f8fafc;
}

.rt-option-selected {
  border-color: #4f46e5 !important;
  background-color: #eef2ff !important;
}

.rt-radio-wrapper {
  padding-top: 2px;
}

.rt-radio-circle {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  border: 2px solid #94a3b8;
  display: inline-block;
  box-sizing: border-box;
  transition: all 0.2s;
}

.rt-radio-checked {
  border-color: #4f46e5;
  background-color: #4f46e5;
  box-shadow: inset 0 0 0 3px #ffffff;
}

.rt-option-content {
  flex: 1;
}

.rt-tag-title {
  font-weight: 600;
  font-size: 15px;
  margin-bottom: 4px;
}

.rt-tag-desc {
  font-size: 12.5px;
  color: #64748b;
  line-height: 1.4;
}

.rt-footer {
  padding: 16px 24px;
  background: #f8fafc;
  border-top: 1px solid #e2e8f0;
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}

.rt-cancel-btn {
  padding: 10px 20px;
  background: #f1f5f9;
  border: 1px solid #cbd5e1;
  color: #475569;
  font-weight: 600;
  font-size: 13px;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
}

.rt-cancel-btn:hover:not(:disabled) {
  background: #e2e8f0;
  color: #0f172a;
}

.rt-save-btn {
  padding: 10px 20px;
  background: #4f46e5;
  border: none;
  color: #ffffff;
  font-weight: 600;
  font-size: 13px;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
  box-shadow: 0 4px 12px rgba(79, 70, 229, 0.3);
}

.rt-save-btn:hover:not(:disabled) {
  background: #4338ca;
  box-shadow: 0 6px 16px rgba(79, 70, 229, 0.4);
}

.rt-save-btn:disabled,
.rt-cancel-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
</style>
